import { ITableFilters } from "../app.interface";
import axiosInstance from "../middlewares/axiosInstance";

export interface Business {
  id: string;
  business_name: string;
  display_id: number;
  contact: {
    address: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };
  is_active: boolean;
  created_at: number;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  phone_number: string;
  is_active: boolean;
  created_at: number;
  last_login_at: number;
}

interface BusinessSearchResponse {
  businesses: Business[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditLogData {
  action: string;
  resource: string;
  details?: any;
  status: "success" | "failure";
  userId?: string;
  ip?: string;
  userAgent?: string;
  error?: string;
  store_id?: string;
}

interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Location {
  _id: string;
  id: string;
  name: string;
  address: string;
  type: "branch" | "department" | "warehouse";
  created_at: number;
  is_deleted: boolean;
}

export interface LocationsResponse {
  warehouses: Location[];
  branches: Location[];
  departments: Location[];
  totals: {
    warehouses: number;
    branches: number;
    departments: number;
    total: number;
  };
}

export const publicService = {
  async searchBusinessesByDisplayId(
    displayId: number
  ): Promise<BusinessSearchResponse> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: BusinessSearchResponse;
      }>("/public/businesses/search", { display_id: displayId });
      return response.data.data;
    } catch (error: any) {
      console.error("Error searching businesses:", error);
      throw new Error(
        error.response?.data?.message || "Failed to search businesses"
      );
    }
  },

  async getUsersByStoreId(
    storeId: string,
    options: {
      page?: number;
      limit?: number;
      searchTerm?: string;
      is_active?: boolean;
      is_pos_user?: boolean;
    } = {}
  ): Promise<UsersResponse> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: UsersResponse;
      }>("/public/users/by-store", {
        store_id: storeId,
        page: options.page || 1,
        limit: options.limit || 10,
        searchTerm: options.searchTerm || "",
        is_pos_user: options.is_pos_user || false,
        is_active: options.is_active !== undefined ? options.is_active : true,
      });
      return response.data.data;
    } catch (error: any) {
      console.error("Error fetching users by store:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch users");
    }
  },

  async getLocations(storeId: string): Promise<LocationsResponse> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: LocationsResponse;
      }>("/public/locations/list", {
        store_id: storeId,
      });
      return response.data.data;
    } catch (error: any) {
      console.error("Error fetching locations:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch locations"
      );
    }
  },

  async exportData(datasource: string, filters?: ITableFilters[]) {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        data: any;
      }>("/reports/export/all", {
        datasource,
        filters,
      });
      return response.data.data;
    } catch (error: any) {
      console.error("Error exportData:", error);
      throw new Error(error.response?.data?.message || "Failed to export data");
    }
  },

  async logAuditEvent(data: AuditLogData): Promise<void> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        message: string;
      }>("/public/logs/audit", data);

      if (!response.data.success) {
        console.warn("Audit event not logged:", response.data.message);
      }
    } catch (error: any) {
      console.error("Error logging audit event:", error.message);
      // Fail silently — don't interrupt user flow
    }
  },
};
